import React, { useState } from 'react';
import '../styles/ProductUploadModal.css';

const ProductUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [uploadMessage, setUploadMessage] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      // Validate file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();

      if (!validTypes.includes(selectedFile.type) && !['xls', 'xlsx'].includes(fileExtension)) {
        setError('Please select a valid Excel file (.xls or .xlsx)');
        setFile(null);
        return;
      }

      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (selectedFile.size > maxSize) {
        setError(`File size exceeds 5MB. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(2)}MB. Please upload a smaller file.`);
        setFile(null);
        e.target.value = ''; // Reset the file input
        return;
      }

      setFile(selectedFile);
      setError(null);
      setUploadResult(null);
      setUploadMessage(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);
    setUploadMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005/api';
      const response = await fetch(`${apiUrl}/admin/products/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok || data.data) {
        // Show results even if some products failed
        setUploadResult(data.data);
        setUploadMessage(data.message);
        setFile(null);

        // Only call success callback if at least some products were imported
        if (data.data.successful > 0) {
          setTimeout(() => {
            if (onUploadSuccess) {
              onUploadSuccess();
            }
          }, 2000);
        }
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    setUploadMessage(null);
    onClose();
  };

  const downloadTemplate = () => {
    // Create a sample CSV template
    const headers = [
      'Catalogue Number',
      'Product Name',
      'Description',
      'Pack Size',
      'Product Image',
      'Product img 2',
      'Product Category',
      'Applications',
      'Product insert',
      'Brand Name',
      'Storage',
      'Source',
      'Dilutions',
      'Clonality',
      'Isotype',
      'Concentration',
      'Immunogen',
      'Reactivity',
      'Sensitivity',
      'Standard',
      'Detection Range',
      'Sample Type',
      'Assay Length',
      'Alternative Names',
      'Test Principle',
      'Research Area',
      'Assay Type'
    ].join('\t');

    const sampleRow = [
      'CAT-001',
      'Sample Product Name',
      'Product description here',
      '100ml',
      'https://example.com/image1.jpg',
      'https://example.com/image2.jpg',
      'Antibodies',
      'Western Blot, ELISA',
      'https://example.com/manual.pdf',
      'Brand Name',
      '2-8°C',
      'Mouse',
      '1:1000',
      'Monoclonal',
      'IgG',
      '1mg/ml',
      'Recombinant protein',
      'Human, Mouse, Rat',
      '10pg/ml',
      'Included',
      '10-1000 pg/ml',
      'Serum, Plasma',
      '3 hours',
      'ALT1, ALT2',
      'Sandwich ELISA',
      'Immunology',
      'ELISA Kit'
    ].join('\t');

    const csvContent = headers + '\n' + sampleRow;

    const blob = new Blob([csvContent], { type: 'text/tab-separated-values' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_upload_template.xls';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Products from Excel</h2>
          <button className="close-btn" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="upload-instructions">
            <h3>Instructions:</h3>
            <ol>
              <li>Download the template file to see the required format</li>
              <li>Fill in your product data in the Excel file</li>
              <li>Required fields: Catalogue Number, Product Name, Product Category</li>
              <li>File size must not exceed 5MB</li>
              <li>Upload the completed Excel file</li>
            </ol>
          </div>

          <div className="template-download">
            <button className="btn btn-secondary" onClick={downloadTemplate}>
              Download Template
            </button>
          </div>

          <div className="file-upload-section">
            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
                id="file-input"
              />
              <label htmlFor="file-input" className="file-input-label">
                {file ? file.name : 'Choose Excel file...'}
              </label>
            </div>

            {file && (
              <div className="file-info">
                <p>Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)</p>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {uploadResult && (
            <div className="upload-results">
              <h3>Upload Results</h3>

              {uploadMessage && (
                <div className={`upload-message ${uploadResult.successful === 0 ? 'message-warning' : 'message-info'}`}>
                  {uploadMessage}
                </div>
              )}

              <div className="results-summary">
                <div className="result-stat">
                  <span className="label">Total:</span>
                  <span className="value">{uploadResult.total}</span>
                </div>
                <div className="result-stat success">
                  <span className="label">Successful:</span>
                  <span className="value">{uploadResult.successful}</span>
                </div>
                <div className="result-stat failed">
                  <span className="label">Failed:</span>
                  <span className="value">{uploadResult.failed}</span>
                </div>
                <div className="result-stat skipped">
                  <span className="label">Skipped:</span>
                  <span className="value">{uploadResult.skipped}</span>
                </div>
              </div>

              {((uploadResult.errors && uploadResult.errors.length > 0) ||
                (uploadResult.failedProducts && uploadResult.failedProducts.length > 0)) && (
                <div className="error-details">
                  <h4>Errors:</h4>
                  <div className="error-list">
                    {/* Handle failedProducts array */}
                    {uploadResult.failedProducts && uploadResult.failedProducts.slice(0, 10).map((err, index) => (
                      <div key={index} className="error-item">
                        <strong>{err.catalogNumber || `Row ${err.row}`}</strong>
                        {err.productName && ` - ${err.productName}`}
                        <div className="error-message-text">
                          {err.error || 'Unknown error'}
                        </div>
                      </div>
                    ))}

                    {/* Handle errors array (old format) */}
                    {uploadResult.errors && uploadResult.errors.slice(0, 10).map((err, index) => (
                      <div key={`error-${index}`} className="error-item">
                        <strong>{err.catalogNumber || `Row ${err.row}`}</strong>
                        {err.productName && ` - ${err.productName}`}
                        <ul>
                          {Array.isArray(err.errors) ? err.errors.map((e, i) => (
                            <li key={i}>{e}</li>
                          )) : <li>{err.error || 'Unknown error'}</li>}
                        </ul>
                      </div>
                    ))}

                    {((uploadResult.errors?.length || 0) + (uploadResult.failedProducts?.length || 0)) > 10 && (
                      <p className="more-errors">
                        ... and {((uploadResult.errors?.length || 0) + (uploadResult.failedProducts?.length || 0)) - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductUploadModal;
